import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import Matching from "./mcq-types/Matching";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://upsc-backend-353e.onrender.com";

const pad2 = (n) => String(n).padStart(2, "0");
const formatLocalDate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayStr = formatLocalDate(new Date());

const letterFor = (idx) => String.fromCharCode(65 + idx); // 0->A
const labelFor = (idx) => `(${letterFor(idx).toLowerCase()})`; // (a)

/** helpers */
const isAnswered = (val) => {
  if (val === undefined || val === null) return false;
  if (typeof val === "string") return val.trim() !== "";
  if (typeof val === "number") return true;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "object") return Object.keys(val).length > 0;
  return false;
};

export default function MCQs() {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchCompleted, setFetchCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultPayload, setResultPayload] = useState(null); // backend evaluation result
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hasSavedSession, setHasSavedSession] = useState(false);
  const [error, setError] = useState("");

  const quizContainerRef = useRef(null);
  const [token, setToken] =
  useState(

    JSON.parse(
      localStorage.getItem("user")
    )?.token || ""
  );

  const user = JSON.parse(localStorage.getItem("user"));
const isPremium = user?.hasPaid;

// 🔥 LOCK TODAY LOGIC
const isTodayLocked = selectedDate === todayStr && !isPremium;
  const navigate = useNavigate();
  // =========================
// Sync Token
// =========================

useEffect(() => {

  const syncUser = () => {

    const updatedUser =
      JSON.parse(

        localStorage.getItem(
          "user"
        )
      );

    setToken(
      updatedUser?.token || ""
    );
  };



  window.addEventListener(
    "userUpdated",
    syncUser
  );



  return () => {

    window.removeEventListener(
      "userUpdated",
      syncUser
    );
  };

}, []);

  // 🧠 Load saved session if available
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("mcqProgress"));
    if (saved && saved.selectedDate === todayStr) {
      setHasSavedSession(true);
    }
  }, []);

  // 🧠 Fetch MCQs (secure — no answers)
  useEffect(() => {
    async function fetchMCQs() {
      try {
        setLoading(true);
setFetchCompleted(false);
        setError("");
        setShowResult(false);
        setResultPayload(null);
        const res = await axios.get(`${API_BASE}/api/user/mcqs?date=${selectedDate}`, {
  headers: {
    Authorization: `Bearer ${token}`,
    "x-today-date": todayStr, // 🔥 ADD THIS
  },
});
        const list = Array.isArray(res.data.mcqs) ? res.data.mcqs : [];
        setQuestions(list);
setCurrent(0);
setAnswers({});
setFetchCompleted(true);
      } catch (err) {
  console.error("MCQ Fetch Error:", err);

  // ALWAYS clear previous state
  setQuestions([]);
  setAnswers({});
  setCurrent(0);
  setShowResult(false);
  setResultPayload(null);
  setShowResult(false);
  setResultPayload(null);
  // mark fetch finished
  setFetchCompleted(true);

  // 🔒 Premium lock
  if (
    err.response?.status === 403 &&
    err.response?.data?.locked
  ) {
    setLoading(false);
    return;
  }

  // 📅 No MCQ uploaded for selected day
  if (
    err.response?.status === 404
  ) {
    setLoading(false);

    toast(
      `No MCQs uploaded for ${selectedDate}`
    );

    return;
  }

  // Other errors
  setLoading(false);

  toast.error(
    err?.response?.data?.error ||
    "Failed to load MCQs"
  );
} finally {
  setLoading(false);
}
    }
    if (token) {

setLoading(false);

if (

selectedDate === todayStr &&

isTodayLocked

){

setQuestions([]);

setFetchCompleted(true);

return;

}

fetchMCQs();

}
  }, [token, selectedDate]);

  // 💾 Persist progress (only attempt state, not results)
  useEffect(() => {
    if (questions.length > 0 && !showResult) {
      localStorage.setItem(
        "mcqProgress",
        JSON.stringify({ selectedDate, answers, showResult })
      );
    }
  }, [answers, showResult, selectedDate, questions]);

  const handleResumeSession = () => {
    const saved = JSON.parse(localStorage.getItem("mcqProgress"));
    if (saved) {
      setSelectedDate(saved.selectedDate);
      setAnswers(saved.answers || {});
      setShowResult(!!saved.showResult);
      setHasSavedSession(false);
    }
  };

  const handleResetSession = () => {
  localStorage.removeItem("mcqProgress");
  setAnswers({});
  setShowResult(false);
  setResultPayload(null);
  setCurrent(0);
  setHasSavedSession(false);
  setSelectedDate(todayStr);
};

  const handleGoBack = () => {
  handleResetSession();
  navigate("/dashboard/mcqs");
};

  const handleAnswer = (qId, value) => {
    setAnswers((prev) => ({ ...prev, [qId]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError("");

      // Build payload: { date, answers: { mcqId: value } }
      const payload = {
        date: selectedDate,
        answers,
      };

      const res = await axios.post(
        `${API_BASE}/api/user/mcqs/check-answers`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResultPayload(
  res.data
);

setShowResult(
  true
);



// =========================
// Live XP Event
// =========================

window.dispatchEvent(

  new CustomEvent(

    "xpGained",

    {

      detail: {

        xp:
  Number(
    res.data?.score || 0
  ) * 5,

        message:
          "MCQ Practice Completed",
      },
    }
  )
);

// once result shown,
// clear saved attempt
localStorage.removeItem(
  "mcqProgress"
);
    } catch (err) {
      console.error("❌ Submit MCQs Error:", err);
      setError(
        err?.response?.data?.error ||
          "Failed to evaluate answers. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFullScreenToggle = () => {
    if (isFullScreen) document.exitFullscreen();
    else quizContainerRef.current?.requestFullscreen();
  };

  useEffect(() => {
    const listener = () =>
      setIsFullScreen(document.fullscreenElement === quizContainerRef.current);
    document.addEventListener("fullscreenchange", listener);
    return () => document.removeEventListener("fullscreenchange", listener);
  }, []);

  const goPrevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(formatLocalDate(d));
  };
  const goNextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    const next = formatLocalDate(d);
    setSelectedDate(next > todayStr ? todayStr : next);
  };

  const hasQuestions = questions.length > 0;
  const q =
  hasQuestions &&
  questions[current]
    ? questions[current]
    : {};

  const answeredCount = useMemo(() => {
    return Object.entries(answers).filter(([id, v]) => isAnswered(v)).length;
  }, [answers]);

  const progress = hasQuestions
    ? Math.round(((current + 1) / questions.length) * 100)
    : 0;

const loadRazorpay = (src) =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const handleUpgradeClick = async () => {
  const res = await loadRazorpay(
    "https://checkout.razorpay.com/v1/checkout.js"
  );

  if (!res) {
    alert("Razorpay SDK failed");
    return;
  }

  try {
    const orderRes = await axios.post(
      `${API_BASE}/api/payments/create-order`,
      {
        amount: 3999,
        type: "mcqPremium",
      },
      {
        headers: { Authorization: `Bearer ${user.token}` },
      }
    );

    const { order } = orderRes.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      name: "UPSC Journey",
      description: "Daily MCQ Premium",
      order_id: order.id,

      handler: async function (response) {
        await axios.post(
          `${API_BASE}/api/payments/verify-payment`,
          {
            razorpay_order_id: order.id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            type: "mcqPremium",
          },
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );

        const updatedUser = { ...user, hasPaid: true };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        alert("✅ Premium Activated!");
        window.location.reload();
      },
    };

    new window.Razorpay(options).open();
  } catch (err) {
    alert("Payment failed");
  }
};

  // 🧩 Resume prompt
  if (hasSavedSession)
    return (
      <div className="flex flex-col items-center justify-center h-[80vh] text-center space-y-4 dark:text-gray-100">
        <h2 className="text-xl font-semibold">
          You have an unfinished MCQ attempt for today.
        </h2>
        <div className="flex gap-4">
          <Button onClick={handleResumeSession}>Resume Attempt</Button>
          <Button variant="outline" onClick={handleResetSession}>
            Start Fresh
          </Button>
        </div>
      </div>
    );

    // =========================
// LOADING STATE
// =========================

if (loading) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh]">

      <div
        className="
          w-14
          h-14
          rounded-full
          border-4
          border-blue-500
          border-t-transparent
          animate-spin
        "
      />

      <h3 className="mt-6 text-lg font-semibold text-gray-700 dark:text-gray-200">
        Checking practice questions...
      </h3>

      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
        Please wait a moment
      </p>

    </div>
  );
}

    // 🔒 TODAY LOCK SCREEN

  if (
  fetchCompleted &&
  !loading &&
  !hasQuestions &&
  !showResult &&
  !isTodayLocked
)
  return (
    <div className="max-w-2xl mx-auto py-10">
      <Card className="bg-white dark:bg-gray-900 border dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-center text-gray-900 dark:text-gray-100">
            No MCQs available for {selectedDate}.
          </CardTitle>
        </CardHeader>

        <CardContent className="flex justify-center gap-3">
          <Button variant="outline" onClick={goPrevDay}>
            ← Previous Day
          </Button>

          <Button variant="outline" onClick={goNextDay}>
            Next Day →
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // =========================
  // ✅ Result screen (secure)
  // =========================
  if (showResult && resultPayload) {
    const { score, total, results } = resultPayload;

    const renderMatchingSingleMultiple = (item) => {
      // correctAnswer is an array of indices (1-based), for single e.g., [3]; userAnswer may be number or array
      const correct = Array.isArray(item.correctAnswer)
        ? item.correctAnswer
        : [];
      const user =
        item.matchingAnswerMode === "single"
          ? (typeof item.userAnswer === "number" ? [item.userAnswer] : [])
          : Array.isArray(item.userAnswer)
          ? item.userAnswer
          : [];

      // Letters string: (a, c)
      const letters = correct
        .map((n) => labelFor(n - 1))
        .join(", ");

      // Full pairs: "Varanasi – Music"
      const pairs = correct
        .map((n) => {
          const i = n - 1;
          const left = item.matchingA?.[i] ?? `Left ${n}`;
          const right = item.matchingB?.[i] ?? `Right ${n}`;
          return `${left} – ${right}`;
        })
        .join("; ");

      // User letters (for display)
      const userLetters = user.map((n) => labelFor(n - 1)).join(", ");     

      return (
        <>
          <div className="mb-1">
            <strong>Correct Answer:</strong> {letters}
            {pairs ? ` → ${pairs}` : ""}
          </div>
          <div className="mb-1">
            <strong>Your Answer:</strong>{" "}
            {user.length ? userLetters : <em>Not answered</em>}
          </div>
        </>
      );
    };

    const renderMatchingMap = (item) => {
      // correctAnswer is object { A:3, ... }, userAnswer too
      const correctMap = item.correctAnswer || {};
      const userMap = item.userAnswer || {};
      const leftLabels = item.matchingA?.map((_, i) => letterFor(i)) || [];

      return (
        <div className="mt-2 space-y-2">
          <div className="font-semibold">Mapping:</div>
          {leftLabels.map((L, idx) => {
            const rightIndex = correctMap[L]; // 1-based
            const userIndex = userMap[L];
            const correctRightText =
              item.matchingB?.[Number(rightIndex) - 1] || `Right ${rightIndex}`;
            const userRightText =
              userIndex != null
                ? item.matchingB?.[Number(userIndex) - 1] || `Right ${userIndex}`
                : null;

            const ok = Number(userIndex) === Number(rightIndex);

            return (
              <div
                key={L}
                className={`flex items-center justify-between border rounded px-3 py-2 ${
                  ok
                    ? "bg-green-50 border-green-300 dark:bg-green-900/30 dark:border-green-700"
                    : "bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-700"
                }`}
              >
                <div className="text-sm">
                  <span className="font-semibold">{L} → {rightIndex}</span>{" "}
                  <span className="text-gray-700 dark:text-gray-300">
                    ({item.matchingA?.[idx] ?? `Left ${L}`} – {correctRightText})
                  </span>
                </div>
                <div className="text-sm">
                  {ok ? (
                    <span className="text-green-700 dark:text-green-300 font-semibold">✅</span>
                  ) : (
                    <span className="text-red-700 dark:text-red-300">
                      ❌ {userIndex ? `Your: ${userIndex} (${userRightText})` : "Not answered"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div
        ref={quizContainerRef}
        className="fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col p-6"
        style={{ boxSizing: "border-box" }}
      >
        <div className="flex items-center justify-between mb-6">

  {/* LEFT SIDE */}
  <button
    onClick={() => navigate("/dashboard")}
    className="group flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 px-5 py-3 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
  >

    {/* Icon */}
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center shadow-lg">

      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2.5}
        stroke="currentColor"
        className="w-5 h-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
        />
      </svg>

    </div>

    {/* Text */}
    <div className="text-left">

      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
        Exit Quiz
      </p>

      <h4 className="font-bold text-gray-900 dark:text-gray-100 leading-tight">
        Back to Dashboard
      </h4>

    </div>

  </button>

  {/* RIGHT SIDE */}
  <Button
    variant="outline"
    onClick={handleFullScreenToggle}
    className="rounded-2xl px-5 py-3 shadow-md hover:shadow-lg transition-all"
  >
    {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
  </Button>

</div>

        <Card className="shadow-xl rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-700 flex-1 overflow-auto">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-gray-900 dark:text-gray-100">
              Result for {selectedDate}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-lg mb-6 text-gray-800 dark:text-gray-200">
              Score:{" "}
              <span className="font-bold">
                {score} / {total}
              </span>{" "}
              ✅
            </div>

            {questions.map((question, idx) => {
  const item = results.find(r => String(r._id) === String(question._id)) || {
    _id: question._id,
    question: question.question,
    correctAnswer: question.correctAnswer,
    explanation: question.explanation,
    userAnswer: answers[question._id] ?? null,
    isCorrect: false
  };

  const original = question;
  item.type =
  original?.type || "normal";

  const answered = isAnswered(item.userAnswer);

let color = "";

if (!answered) {
  // 🟡 Unattempted
  color =
    "bg-yellow-50 border-yellow-400 text-yellow-800 " +
    "dark:bg-yellow-900 dark:border-yellow-600 dark:text-yellow-300";
} else if (item.isCorrect) {
  // 🟢 Correct
  color =
    "bg-green-50 border-green-400 text-green-700 " +
    "dark:bg-green-900 dark:border-green-600 dark:text-green-300";
} else {
  // 🔴 Wrong
  color =
    "bg-red-50 border-red-400 text-red-700 " +
    "dark:bg-red-900 dark:border-red-600 dark:text-red-300";
}

  return (
    <div
      key={item._id}
      className={`border rounded-lg p-4 mb-4 ${color} border-gray-300 dark:border-gray-700`}
    >

      {/* Question */}
      <div className="mb-3 text-gray-900 dark:text-gray-100">

  {item.type === "assertion-reason" ? (
    <div className="space-y-3">

      <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
        <strong className="text-blue-700 dark:text-blue-300">
          Assertion (A):
        </strong>

        <p className="mt-1">
          {original?.assertion}
        </p>
      </div>

      <div className="p-3 rounded-lg border border-purple-200 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20">
        <strong className="text-purple-700 dark:text-purple-300">
          Reason (R):
        </strong>

        <p className="mt-1">
          {original?.reason}
        </p>
      </div>

    </div>
    ) : (
    <div className="text-base font-medium whitespace-pre-line">
      {idx + 1}. {item.question}
    </div>
  )}

</div>

      {/* Statements */}
      {original?.type === "statement" && original?.statements?.length > 0 && (
        <div className="mb-2 text-sm text-gray-800 dark:text-gray-200">
          <strong>Statements:</strong>
          <ol className="ml-6 list-decimal">
            {original.statements.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Options (Non-matching MCQs) */}
      {item.type !== "matching" && original?.options?.length > 0 && (
        <ul className="mb-2 list-none">
          {original.options.map((opt, oidx) => {
            const isUser = String(item.userAnswer) === String(opt);
            const isCorrect = String(item.correctAnswer) === String(opt);

            return (
              <li
                key={oidx}
                className={`p-1 rounded flex items-center gap-2 ${
                  isUser ? "font-semibold ring-2 ring-blue-400" : ""
                } ${isCorrect ? "text-green-700" : ""}`}
              >
                <input type="radio" disabled checked={isUser} readOnly />
                <span>{opt}</span>
                {isCorrect && (
                  <span className="ml-1 text-green-700 font-bold">
                    (Correct)
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* MATCHING MCQ — Display both lists */}
      {item.type === "matching" && (
        <>
          <div className="space-y-1 mb-2 text-sm">
            {original?.matchingA?.map((aItem, i) => (
              <p key={i}>
                <span className="font-bold">{letterFor(i)}.</span>{" "}
                {aItem} – {original?.matchingB?.[i]}
              </p>
            ))}
          </div>

          {/* Matching evaluation */}
          {item?.matchingAnswerMode === "map"
            ? renderMatchingMap(item)
            : renderMatchingSingleMultiple(item)}
        </>
      )}

      {/* Your answer */}
      <div className="mb-1">
        <strong>Your answer: </strong>
        {answered ? String(item.userAnswer) : <em>Not answered</em>}
      </div>

      {/* Correct answer */}
      <div className="mb-1">
        <strong>Correct answer: </strong>
        {Array.isArray(item.correctAnswer)
          ? item.correctAnswer.join(", ")
          : String(item.correctAnswer)}
      </div>

      {/* Explanation */}
      {item.explanation && (
        <p className="mt-2 italic text-gray-700 dark:text-gray-300">
          💡 {item.explanation}
        </p>
      )}
    </div>
  );
})}

            <div className="flex justify-center mt-6 gap-3">
              <Button onClick={handleResetSession}>Reattempt</Button>
              <Button variant="outline" onClick={handleGoBack}>
                Go Back to MCQ Section
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // =========================
  // ✅ Question View
  // =========================
  return (
    <div
      ref={quizContainerRef}
      className={`mx-auto bg-inherit ${
        isFullScreen
          ? "fixed inset-0 w-full h-full max-w-none p-6 overflow-auto bg-white dark:bg-gray-900 z-50 flex flex-col"
          : "max-w-2xl py-10"
      }`}
      style={isFullScreen ? { boxSizing: "border-box" } : undefined}
    >
      <div className="flex items-center justify-between mb-6">

  {/* LEFT SIDE */}
  <button
    onClick={() => navigate("/dashboard")}
    className="group flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 px-5 py-3 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
  >

    {/* Icon */}
    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center shadow-lg">

      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2.5}
        stroke="currentColor"
        className="w-5 h-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
        />
      </svg>

    </div>

    {/* Text */}
    <div className="text-left">

      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
        Exit MCQs
      </p>

      <h4 className="font-bold text-gray-900 dark:text-gray-100 leading-tight">
        Back to Dashboard
      </h4>

    </div>

  </button>

  {/* RIGHT SIDE */}
  <div className="flex items-center gap-3">

    {/* Progress */}
    {!showResult && (
      <div className="hidden md:flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-2xl font-semibold shadow-sm border border-blue-200 dark:border-blue-800">
        <span>📘</span>
        {answeredCount}/{questions.length} Answered
      </div>
    )}

    {/* Fullscreen */}
    <Button
      variant="outline"
      onClick={handleFullScreenToggle}
      className="rounded-2xl px-5 py-3 shadow-md hover:shadow-lg transition-all"
    >
      {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
    </Button>

  </div>

</div>

      <div className="flex justify-center items-center gap-3 mb-6">
        <Button variant="outline" onClick={goPrevDay}>
          ← Previous Day
        </Button>
        <input
          type="date"
          value={selectedDate}
          max={todayStr}
          onChange={(e) =>
            setSelectedDate(
              e.target.value > todayStr ? todayStr : e.target.value
            )
          }
          className="border rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
        />
        <Button variant="outline" onClick={goNextDay} disabled={selectedDate >= todayStr}>
          Next Day →
        </Button>
      </div>

      <Progress value={hasQuestions ? Math.round((answeredCount / questions.length) * 100) : 0} className="mb-6 h-2" />

      <Card className="shadow-xl rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-700 flex-grow overflow-auto">
        <CardHeader>
          <CardTitle className="text-lg text-gray-900 dark:text-gray-100">
            Question {current + 1} of {questions.length} — {selectedDate}
          </CardTitle>
        </CardHeader>
        <CardContent>
  {isTodayLocked ? (
    <div className="text-center py-12">
      <div className="text-5xl mb-4">🔒</div>

      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-3">
        Today's MCQs Locked
      </h2>

      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Upgrade to access today's premium MCQs.
      </p>

      <button
        onClick={handleUpgradeClick}
        className="px-6 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
      >
        Upgrade Now
      </button>

      <div className="mt-6">
        <Button variant="outline" onClick={goPrevDay}>
          ← Practice Previous Day
        </Button>
      </div>
    </div>
  ) : (
    <>
      <div className="mb-4 text-gray-900 dark:text-gray-100">

  {q.type === "assertion-reason" ? (
    <div className="space-y-4">

      <div className="p-4 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
        <h4 className="font-bold mb-2 text-blue-700 dark:text-blue-300">
          Assertion (A)
        </h4>

        <p className="text-base">
          {q.assertion}
        </p>
      </div>

      <div className="p-4 rounded-xl border border-purple-200 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20">
        <h4 className="font-bold mb-2 text-purple-700 dark:text-purple-300">
          Reason (R)
        </h4>

        <p className="text-base">
          {q.reason}
        </p>
      </div>

    </div>
    ) : (
  <div className="text-base font-medium overflow-x-auto">
    {(q?.question || "Question unavailable")
      .split("\n")
      .map((line, index) => (
        <div key={index} className="whitespace-nowrap">
          {line}
        </div>
      ))}
  </div>
)}

</div>

      {q?.type === "statement" && q?.statements?.length > 0 && (
        <div className="mb-3 text-sm text-gray-800 dark:text-gray-200">
          {q.statements.map((stmt, i) => (
            <p key={i}>
              <strong>{i + 1}.</strong>{" "}
              {stmt}
            </p>
          ))}
        </div>
      )}

      {q?.type === "matching" ? (
        <Matching
          mcq={q}
          value={
            answers[q._id] ||
            (q.matchingAnswerMode === "map"
              ? {}
              : q.matchingAnswerMode === "multiple"
              ? []
              : null)
          }
          setValue={(val) => handleAnswer(q._id, val)}
          submitted={false}
        />
      ) : (
        <div className="space-y-2">
          {q.options?.map((opt, idx) => (
            <label
              key={`${q._id}_opt_${idx}`}
              className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition ${
                answers[q._id] === opt
                  ? "border-blue-400 bg-blue-50 dark:bg-blue-900"
                  : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              }`}
            >
              <input
                type="radio"
                name={`q_${q._id}`}
                value={opt}
                checked={answers[q._id] === opt}
                onChange={(e) => handleAnswer(q._id, e.target.value)}
                className="h-4 w-4"
              />
              <span className="text-sm text-gray-800 dark:text-gray-200">
                {opt}
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mt-6">
        <Button
          onClick={() => setCurrent((p) => p - 1)}
          disabled={current === 0}
        >
          Previous
        </Button>

        <div className="text-sm text-gray-600 dark:text-gray-300">
          Answered {answeredCount}/{questions.length}
        </div>

        {current === questions.length - 1 ? (
          <Button
  onClick={handleSubmit}
  disabled={submitting || answeredCount === 0}
>
  {submitting
    ? "Submitting..."
    : answeredCount === 0
    ? "Answer at least 1 question"
    : "Submit Quiz"}
</Button>
        ) : (
          <Button onClick={() => setCurrent((p) => p + 1)}>
            Next
          </Button>
        )}
      </div>

      {current !== questions.length - 1 && (
        <div className="mt-4 flex justify-end">
          <Button
  variant="outline"
  onClick={handleSubmit}
  disabled={submitting || answeredCount === 0}
>
            {submitting
  ? "Submitting..."
  : answeredCount === 0
  ? "Answer at least 1 question"
  : "Submit Now"}
          </Button>
        </div>
      )}
    </>
  )}
</CardContent>
      </Card>
    </div>
  );
}
